import { loadEnvConfig } from "@happyvertical/utils";
//#region src/adapter.ts
/**
* Logger Adapter - Converts signals to structured log messages
*
* Transforms signals from the SMRT framework into structured log entries.
* Each signal type is mapped to an appropriate log level:
* - start → debug
* - step → debug
* - end → info
* - error → error
*
* @example
* ```typescript
* const logger = new ConsoleLogger('info');
* const adapter = new LoggerAdapter(logger);
* signalBus.register(adapter);
* ```
*/
var LoggerAdapter = class {
	logger;
	constructor(logger) {
		this.logger = logger;
	}
	/**
	* Handle a signal and log appropriately
	*
	* @param signal - Signal to log
	*/
	async handle(signal) {
		const context = {
			id: signal.id,
			objectId: signal.objectId,
			className: signal.className,
			method: signal.method,
			timestamp: signal.timestamp
		};
		if (signal.duration !== void 0) context.duration = signal.duration;
		if (signal.metadata) context.metadata = signal.metadata;
		switch (signal.type) {
			case "start":
				this.logger.debug(`${signal.className}.${signal.method}() started`, context);
				break;
			case "step":
				this.logger.debug(`${signal.className}.${signal.method}() step: ${signal.step || "unknown"}`, context);
				break;
			case "end":
				this.logger.info(`${signal.className}.${signal.method}() completed in ${signal.duration}ms`, {
					...context,
					result: signal.result !== void 0 ? "present" : "none"
				});
				break;
			case "error":
				this.logger.error(`${signal.className}.${signal.method}() failed: ${signal.error?.message || "Unknown error"}`, {
					...context,
					error: signal.error ? {
						message: signal.error.message,
						name: signal.error.name,
						stack: signal.error.stack
					} : void 0
				});
				break;
		}
	}
};
//#endregion
//#region src/console.ts
/**
* Console-based logger with level filtering
*
* Logs are written to console with appropriate severity levels.
* Messages are only output if they meet the configured log level threshold.
*
* @example
* ```typescript
* const logger = new ConsoleLogger('info');
* logger.debug('Debug message');  // Not output (below 'info')
* logger.info('Info message');    // Output
* logger.error('Error message');  // Output
* ```
*/
var ConsoleLogger = class ConsoleLogger {
	level;
	static LEVELS = [
		"debug",
		"info",
		"warn",
		"error"
	];
	constructor(level = "info") {
		this.level = level;
	}
	/**
	* Check if a log level should be output
	*
	* @param level - Log level to check
	* @returns True if level meets threshold
	*/
	shouldLog(level) {
		const currentIndex = ConsoleLogger.LEVELS.indexOf(this.level);
		return ConsoleLogger.LEVELS.indexOf(level) >= currentIndex;
	}
	/**
	* Format context for console output
	*
	* @param context - Structured metadata
	* @returns Formatted context string
	*/
	formatContext(context) {
		if (!context || Object.keys(context).length === 0) return "";
		return ` ${JSON.stringify(context)}`;
	}
	debug(message, context) {
		if (this.shouldLog("debug")) console.debug(`[DEBUG] ${message}${this.formatContext(context)}`);
	}
	info(message, context) {
		if (this.shouldLog("info")) console.info(`[INFO] ${message}${this.formatContext(context)}`);
	}
	warn(message, context) {
		if (this.shouldLog("warn")) console.warn(`[WARN] ${message}${this.formatContext(context)}`);
	}
	error(message, context) {
		if (this.shouldLog("error")) console.error(`[ERROR] ${message}${this.formatContext(context)}`);
	}
};
//#endregion
//#region src/index.ts
/**
* No-op logger that discards all log messages
*
* Used when logging is disabled (config: false)
*/
var NoopLogger = class {
	debug(_message, _context) {}
	info(_message, _context) {}
	warn(_message, _context) {}
	error(_message, _context) {}
};
/**
* Create a logger from configuration
*
* Supports environment variable configuration via HAVE_LOGGER_LEVEL.
* User-provided options take precedence over environment variables.
*
* @param config - Logger configuration (boolean or object)
* @returns Configured logger instance
*
* @example
* ```typescript
* // Console logger with 'info' level (default)
* const logger1 = createLogger(true);
*
* // Console logger with level from HAVE_LOGGER_LEVEL env var
* process.env.HAVE_LOGGER_LEVEL = 'debug';
* const logger2 = createLogger(true); // Uses 'debug' from env
*
* // No-op logger (all log calls are discarded)
* const logger3 = createLogger(false);
*
* // Console logger with 'debug' level (overrides env)
* const logger4 = createLogger({ level: 'debug' });
*
* // Custom log level (user options take precedence)
* process.env.HAVE_LOGGER_LEVEL = 'info';
* const logger5 = createLogger({ level: 'warn' }); // Uses 'warn' not 'info'
* ```
*/
function createLogger(config) {
	if (typeof config === "boolean") {
		if (!config) return new NoopLogger();
		return new ConsoleLogger(loadEnvConfig({}, {
			packageName: "logger",
			schema: { level: "string" }
		}).level || "info");
	}
	return new ConsoleLogger(loadEnvConfig(config, {
		packageName: "logger",
		schema: { level: "string" }
	}).level || "info");
}
/** @internal */
var PACKAGE_VERSION_INITIALIZED = true;
//#endregion
export { ConsoleLogger, LoggerAdapter, PACKAGE_VERSION_INITIALIZED, createLogger };

//# sourceMappingURL=index.js.map